import { useEffect, useState } from 'react';

import { buttons } from '@/styles/buttons';

import { EditIcon } from '@/ui/Icons/EditIcon';

import { classNames } from '@/utils/classnames';

import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '../../../ui/MatchaTooltip';
import { PercentageInput } from './PercentageInput';

export type LimitPriceTokenSide = 'buy' | 'sell';

// The custom limit-price offset is bounded below just above -100% so the
// resulting rate can never reach zero. There is no upper bound.
const MIN_CUSTOM_PERCENT = -99.99;

export type Shortcut = {
  id: string;
  label: string;
  multiplier: number;
};

const MARKET_SHORTCUT_ID = 'market';

const LIMIT_SHORTCUTS: Shortcut[] = [
  { id: MARKET_SHORTCUT_ID, label: 'Market', multiplier: 0 },
  { id: '2p', label: '2%', multiplier: 0.02 },
  { id: '5p', label: '5%', multiplier: 0.05 },
  { id: '10p', label: '10%', multiplier: 0.1 },
];

const TRIGGER_SHORTCUTS: Shortcut[] = [
  { id: '5p', label: '5%', multiplier: 0.05 },
  { id: '10p', label: '10%', multiplier: 0.1 },
  { id: '25p', label: '25%', multiplier: 0.25 },
  { id: '50p', label: '50%', multiplier: 0.5 },
];

const buttonDesciption = (shortcut: Shortcut, limitPriceTokenSide: LimitPriceTokenSide) => {
  if (shortcut.id === MARKET_SHORTCUT_ID) {
    return 'Market price';
  } else if (limitPriceTokenSide === 'buy') {
    return `${shortcut.multiplier * 100}% below market price`;
  } else if (limitPriceTokenSide === 'sell') {
    return `${shortcut.multiplier * 100}% above market price`;
  }
};

const ShortcutButton = ({
  shortcut,
  disabled,
  limitPriceTokenSide,
  onClick,
}: {
  shortcut: Shortcut;
  disabled: boolean;
  limitPriceTokenSide: LimitPriceTokenSide;
  onClick(multiplier: number): void;
}) => {
  const description = buttonDesciption(shortcut, limitPriceTokenSide);

  return (
    <MatchaTooltip>
      <MatchaTooltipTrigger>
        <button
          className={buttons({
            size: 'small',
            shape: 'pill',
            color: 'alphaGray',
            class: 'px-2 grow',
          })}
          disabled={disabled}
          onClick={() => onClick(shortcut.multiplier)}
          aria-label={description}
        >
          {shortcut.id !== MARKET_SHORTCUT_ID && (
            <span>{limitPriceTokenSide === 'sell' ? '+' : '-'}</span>
          )}
          {shortcut.label}
        </button>
      </MatchaTooltipTrigger>
      <MatchaTooltipContent>{description}</MatchaTooltipContent>
      <MatchaTooltipPopover sideOffset={8} />
    </MatchaTooltip>
  );
};

type LimitPriceShortcutsProps = {
  limitPriceTokenSide: LimitPriceTokenSide;
  disabled: boolean;
  onChange: (multiplier: number) => void;
  /** When set, shortcuts always move above or below market, ignoring the displayed side. */
  bias?: 'above' | 'below';
};

export const LimitPriceShortcuts = ({
  limitPriceTokenSide,
  disabled,
  onChange,
  bias,
}: LimitPriceShortcutsProps) => {
  const priceBias: 'above' | 'below' = bias ?? (limitPriceTokenSide === 'sell' ? 'above' : 'below');
  const shortcuts = bias ? TRIGGER_SHORTCUTS : LIMIT_SHORTCUTS;
  // Signed percentage offset for the custom field (positive = above market on
  // the sell side, negative = below market on the buy side). `null` means no
  // custom offset is set (a shortcut is driving the rate instead).
  const [customPercent, setCustomPercent] = useState<number | null>(null);

  // Apply the sign dictated by the trade side (buy is always negative, sell
  // always positive regardless of what was typed). Below-market values are
  // floored just above -100% so a large buy magnitude can't drive the rate
  // to zero.
  const applySideSign = (percent: number) => {
    const magnitude = Math.abs(percent);
    return priceBias === 'above' ? magnitude : Math.max(-magnitude, MIN_CUSTOM_PERCENT);
  };

  const handleCustomPercentChange = (percent: number) => {
    const signed = applySideSign(percent);
    setCustomPercent(signed);
    onChange(signed / 100);
  };

  const handleCustomPercentClear = () => {
    setCustomPercent(null);
    onChange(0);
  };

  // Re-sign the custom offset when flipping trade direction so the rate follows
  // the new side.
  useEffect(() => {
    if (customPercent === null) return;
    const signed = applySideSign(customPercent);
    if (signed === customPercent) return;
    setCustomPercent(signed);
    onChange(signed / 100);
    // `applySideSign` is derived from `priceBias`, captured below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceBias, customPercent, onChange]);

  const handleShortcutClick = (multiplier: number) => {
    setCustomPercent(null);
    onChange(priceBias === 'above' ? multiplier : multiplier * -1);
  };

  return (
    <div
      className="flex flex-row gap-2 mt-4 min-w-0"
      aria-label="Limit sell price shortcuts"
      role="list"
    >
      {shortcuts.map((shortcut) => (
        <ShortcutButton
          key={shortcut.id}
          shortcut={shortcut}
          disabled={disabled}
          limitPriceTokenSide={priceBias === 'above' ? 'sell' : 'buy'}
          onClick={handleShortcutClick}
        />
      ))}
      <div className="min-w-0">
        <PercentageInput
          value={customPercent}
          onChange={handleCustomPercentChange}
          onClear={handleCustomPercentClear}
          disabled={disabled}
          min={Number.NEGATIVE_INFINITY}
          max={Number.POSITIVE_INFINITY}
          placeholder="0"
          ariaLabel="Custom limit price percentage"
          icon={
            <div className="relative size-4 shrink-0 mr-1">
              <EditIcon
                className={classNames(
                  disabled ? 'text-token-gray-600' : 'text-token-gray-800',
                  'size-4',
                )}
              />
            </div>
          }
        />
      </div>
    </div>
  );
};
